import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { format } from 'date-fns';
import {
  LngLatBounds,
  Map,
  Marker,
  NavigationControl,
  Popup,
} from 'maplibre-gl';
import { createMap } from 'maplibre-gl-js-amplify';
import numeral from 'numeral';
import {
  ChartPointViewModel,
  DeviceInfoViewModel,
  LocationModel,
  LocationSelectionViewModel,
  SensingType,
  SensingViewModel,
} from '../device-detail-summary/device-detail-summary.component';
import { CustomFullscreenControl } from '@utils/map-fullscreen-control.util';

/** 位置情報のViewModel */
export type LocationViewModel = LocationModel;

/** 複数センサの計測結果のViewModel */
export type DeviceLocationViewModel = DeviceInfoViewModel;

/** センサーのオプション */
type SensorOption = {
  label: string;
  unit: string;
};

@Component({
  selector: 'app-device-detail-map',
  standalone: true,
  imports: [],
  templateUrl: './device-detail-map.component.html',
  styleUrl: './device-detail-map.component.scss',
})
export class DeviceDetailMapComponent implements OnInit, OnChanges, OnDestroy {
  /** 位置情報 */
  @Input({ required: true }) locationList: DeviceLocationViewModel[] = [];

  /** 電源表示用のLocation履歴 */
  @Input({ required: true }) batteryLocationList: DeviceLocationViewModel[] =
    [];

  /** デバイス名 */
  @Input({ required: true }) deviceName: string = '';

  /** センサータイプ */
  @Input({ required: true }) sensingType: SensingType | '' | undefined;

  /** チャートポイント */
  @Input() chartPoints: ChartPointViewModel | undefined;

  private map!: Map;
  private markers: Marker[] = [];
  private routeMarkers: Marker[] = [];
  private mapContainer: HTMLElement | null = null;
  private bodyOverflow: string | null = null;
  private readonly fullscreenBodyClass = 'map-fullscreen-active';

  private readonly sensorOptions: { [key: string]: SensorOption } = {
    temperature: {
      label: '温度',
      unit: '℃',
    },
    humidity: {
      label: '湿度',
      unit: '%',
    },
    pressure: {
      label: '気圧',
      unit: 'hPa',
    },
    light: {
      label: '照度',
      unit: 'lx',
    },
    tilt: {
      label: '傾斜',
      unit: '°',
    },
  };

  private readonly eventSensingKeys: string[] = [
    'fallDetected',
    'mcdDetected',
    'lightExposure',
    'shockDetected',
  ];

  private readonly markerColors: { [key: string]: string } = {
    alert: '#EF4444',
    prealert: '#FFD700',
    normal: '#4169E1',
    noValue: '#B7B7B7',
  };

  private routeColor = '#606060';

  private highlightLocationList: DeviceLocationViewModel[] = [];
  private highlightMakers: Marker[] = [];
  private highlightPopup: Popup | null = null;

  async ngOnInit() {
    this.map = await createMap({
      container: 'map',
      center: [139.767, 35.681],
      zoom: 11,
      minZoom: 1,
    });

    this.map.addControl(
      new NavigationControl({
        visualizePitch: true,
      }),
    );

    this.mapContainer = this.map.getContainer();
    // ブラウザUIを残すため自作のフルスクリーンコントロールで制御する
    this.map.addControl(
      new CustomFullscreenControl((enabled) => {
        this.setFullscreen(enabled);
      }),
    );

    // arrowhead
    const arrowHeadImage = await this.createArrowHeadImage(40, this.routeColor);
    this.map.addImage('arrow-head', arrowHeadImage);
  }

  ngOnDestroy(): void {
    this.clearFullscreen();
  }

  /**
   *地図DOM（this.mapContainer）に拡大表示用のcssクラスを付与する。
   */
  private setFullscreen(enabled: boolean): void {
    const className = 'map-fullscreen';
    if (this.mapContainer) {
      this.mapContainer.classList.toggle(className, enabled);
    }
    // サイドバー制御用クラスを付与
    document.body.classList.toggle(this.fullscreenBodyClass, enabled);
    if (enabled) {
      if (this.bodyOverflow === null) {
        this.bodyOverflow = document.body.style.overflow;
      }
      document.body.style.overflow = 'hidden';
    } else if (this.bodyOverflow !== null) {
      document.body.style.overflow = this.bodyOverflow;
      this.bodyOverflow = null;
    }
    requestAnimationFrame(() => this.map.resize());
  }

  private clearFullscreen(): void {
    // 画面遷移時に疑似フルスクリーン状態を残さない
    document.body.classList.remove(this.fullscreenBodyClass);
    if (this.bodyOverflow !== null) {
      document.body.style.overflow = this.bodyOverflow;
      this.bodyOverflow = null;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['locationList'] !== undefined ||
      changes['batteryLocationList'] !== undefined
    ) {
      this.chartPoints = undefined;
      if (this.highlightPopup) {
        this.highlightPopup.remove();
        this.highlightPopup = null;
      }
      if (this.highlightLocationList.length > 0) {
        this.highlightMakers.forEach((marker) => marker.remove());
        this.highlightLocationList = [];
      }
      // マップに情報を表示する
      const locationList = this.getSelectedLocationList();
      if (this.sensingType != null) {
        this.initMapLayers(locationList, this.sensingType).then();
      }
    } else if (changes['sensingType'] !== undefined) {
      // 初期化
      this.chartPoints = undefined;
      if (this.highlightPopup) {
        this.highlightPopup.remove();
        this.highlightPopup = null;
      }
      if (this.highlightLocationList.length > 0) {
        this.highlightMakers.forEach((marker) => marker.remove());
        this.highlightLocationList = [];
      }
      const sensingType: SensingType = changes['sensingType'].currentValue;
      const panEnabled = changes['sensingType'].previousValue == null;
      const locationList = this.getSelectedLocationList(sensingType);
      if (this.sensingType != null) {
        this.initMapLayers(locationList, sensingType, panEnabled).then();
      }
    } else if (changes['chartPoints'] !== undefined) {
      if (this.highlightLocationList.length > 0) {
        this.highlightMakers.forEach((marker) => marker.remove());
      }
      const locationList = this.getSelectedLocationList();
      if (locationList.length && this.sensingType != null) {
        this.initMapLayers(locationList, this.sensingType, false).then();
      }
    }
  }

  private getSelectedLocationList(
    sensingType = this.sensingType,
  ): DeviceLocationViewModel[] {
    return sensingType === 'battery'
      ? this.batteryLocationList
      : this.locationList;
  }

  private async waitMapLoaded(): Promise<boolean> {
    if (this.map === undefined) {
      return false;
    } else if (this.map.loaded()) {
      return true;
    }

    return new Promise<boolean>((resolve) => {
      this.map.on('load', () => {
        resolve(true);
      });
    });
  }

  private async initMapLayers(
    locationList: DeviceLocationViewModel[],
    sensingType: SensingType | '',
    pan = true,
  ) {
    // マップが表示するまで待機
    if (!(await this.waitMapLoaded())) {
      return;
    }

    // マーカー表示
    this.setMarkers(locationList, sensingType);

    // ルート表示
    this.drawRoutes(this.routeMarkers);

    // PANする
    if (pan) {
      this.panToMarkers(this.markers);
    }
  }

  private setMarkers(
    locationList: DeviceLocationViewModel[],
    sensingType: SensingType | '',
  ) {
    // 初期化
    this.markers.forEach((marker) => marker.remove());
    this.markers = [];
    this.routeMarkers = [];
    this.highlightMakers.forEach((marker) => marker.remove());
    this.highlightMakers = [];
    this.highlightLocationList = [];
    this.highlightPopup?.remove();
    this.highlightPopup = null;
    const selectedChartPoint = this.chartPoints;

    if (
      selectedChartPoint &&
      sensingType === 'battery' &&
      this.isLocationSelection(selectedChartPoint)
    ) {
      this.highlightLocationList = locationList.filter((value) => {
        const location = value.location;
        return (
          location?.timeStamp === selectedChartPoint.locationTimeStamp &&
          location.longitude === selectedChartPoint.longitude &&
          location.latitude === selectedChartPoint.latitude
        );
      });
    } else if (
      selectedChartPoint &&
      sensingType !== '' &&
      sensingType !== 'battery' &&
      !this.isLocationSelection(selectedChartPoint)
    ) {
      const location = locationList.filter(
        (value) =>
          value[sensingType]?.eventAt === selectedChartPoint.eventAt &&
          value[sensingType]?.value === selectedChartPoint.value,
      );

      this.updateHighlightLocation(location, sensingType);
    }

    // マーカー追加
    this.createMarker(locationList, sensingType, true);

    this.createMarker(this.highlightLocationList, sensingType, false);
  }

  private createMarker(
    locationList: DeviceLocationViewModel[],
    sensingType: SensingType | '',
    insert: boolean = false,
  ) {
    for (const [index, location] of locationList.entries()) {
      const longitude = location.location?.longitude;
      const latitude = location.location?.latitude;
      // 位置情報がない場合（null or undefined）の場合はマーカーを追加しない
      if (longitude == null || latitude == null) {
        continue;
      }

      const eventAt = location.location?.eventAt;
      let sensor: SensingViewModel | undefined;
      if (sensingType !== '' && sensingType !== 'battery') {
        sensor = location[sensingType];
      }

      const markerScale =
        !insert && index === locationList.length - 1 ? 1.5 : 1.0;

      let sensorStatus = sensor?.status?.toLowerCase();
      if (!sensorStatus) {
        sensorStatus = sensor?.value ? 'normal' : 'noValue';
        if (this.eventSensingKeys.includes(sensingType)) {
          sensorStatus = 'normal';
        }
      }
      let markerColor = this.markerColors[sensorStatus];

      if (sensingType === 'battery') {
        markerColor = this.markerColors['normal'];
      }

      if (
        this.eventSensingKeys.includes(sensingType) &&
        sensor?.eventAt != null
      ) {
        // 画面を開いた際に直近の情報取得する際、検知系のstatus情報がnullのため
        markerColor = this.markerColors['alert'];
      }

      const marker = new Marker({ color: markerColor, scale: markerScale })
        .setLngLat([longitude, latitude])
        .addTo(this.map);

      const popup = new Popup({
        closeButton: false,
        closeOnClick: !insert,
        closeOnMove: !insert,
      });
      marker.getElement().addEventListener('mouseenter', () => {
        marker.getElement().style.cursor = 'pointer';

        const description = this.createPointDescription(
          sensingType,
          sensor?.value ?? null,
          sensor?.eventType ?? '',
          sensor?.eventTrigger ?? '',
          eventAt,
          location.location?.address,
          sensor?.threshold,
          location.location,
        );

        popup
          .setLngLat([longitude, latitude])
          .setHTML(description)
          .setOffset([0, -20])
          .addTo(this.map);
      });

      marker.getElement().addEventListener('mouseleave', () => {
        marker.getElement().style.cursor = '';
        popup.remove();
      });

      if (!insert) {
        if (this.highlightPopup) {
          this.highlightPopup.remove();
          this.highlightPopup = null;
        }

        if (index === locationList.length - 1) {
          const description = this.createPointDescription(
            sensingType,
            sensor?.value ?? null,
            sensor?.eventType ?? '',
            sensor?.eventTrigger ?? '',
            eventAt,
            location.location?.address,
            sensor?.threshold,
            location.location,
          );

          popup
            .setLngLat([longitude, latitude])
            .setHTML(description)
            .setOffset([0, -20])
            .addTo(this.map);

          this.highlightPopup = popup;
          popup.on('close', () => this.handleHighlightPopupClose(popup));
        }
        this.highlightMakers.push(marker);
      } else {
        this.markers.push(marker);
        if (sensingType !== 'battery' || location.location?.timeStamp != null) {
          this.routeMarkers.push(marker);
        }
      }
    }
  }

  /** 強調表示の吹き出しが閉じたら、拡大マーカーを通常表示へ戻す。 */
  private handleHighlightPopupClose(popup: Popup): void {
    if (this.highlightPopup !== popup) {
      return;
    }

    this.highlightMakers.forEach((marker) => marker.remove());
    this.highlightMakers = [];
    this.highlightLocationList = [];
    this.highlightPopup = null;
  }

  private createPointDescription(
    sensingType: SensingType | '',
    value: number | null,
    eventType: string | '',
    eventTrigger: string | '',
    eventAt: string | null = null,
    address?: string | null,
    threshold?: number,
    location?: LocationViewModel | null,
  ): string {
    if (sensingType === 'battery') {
      const locationTimestamp =
        location?.timeStamp == null
          ? '-'
          : format(new Date(location.timeStamp), 'yyyy/MM/dd HH:mm:ss');
      return `
        <b>${this.deviceName}</b>
        <div>${locationTimestamp}</div>
        <div>住所：${location?.address ?? '-'}</div>
        <div>緯度：${location?.latitude ?? '-'}</div>
        <div>経度：${location?.longitude ?? '-'}</div>
        <div>Location Tech Type：${location?.locationTechType ?? '-'}</div>
        <div>RAT：${location?.radioAccessTechnology ?? '-'}</div>
      `;
    }

    const sensorOption = this.sensorOptions[sensingType];
    let sensorDescription = '';
    if (sensorOption) {
      const label = sensorOption.label;
      const unit = sensorOption.unit;
      if (value == null) {
        sensorDescription = `${label}：-`;
      } else {
        const formattedValue = numeral(value).format('0.0');
        sensorDescription = `${label}：${formattedValue}&nbsp;${unit}`;
      }
    } else {
      sensorDescription = this.getEventDescription(
        sensingType,
        value,
        eventType,
        eventTrigger,
        threshold,
      );
    }

    const eventAtTimestamp =
      eventAt == null ? '-' : format(new Date(eventAt), 'yyyy/MM/dd HH:mm:ss');

    const description = `
        <b>${this.deviceName}</b>
        <div>${eventAtTimestamp}</div>
        <div>${sensorDescription}</div>
        <div>住所：${address ?? ''}</div>
    `;

    return description;
  }

  /**
   * イベント検知の説明を生成する
   * @param sensingValue センサの計測結果
   * @param sensingType 計測種別
   * @returns イベント検知の説明文
   */
  private getEventDescription(
    sensingType: SensingType | '',
    value: number | null,
    eventType: string | '',
    eventTrigger: string | '',
    threshold?: number,
  ): string {
    let alertDescription = '';

    if (sensingType === 'fallDetected') {
      alertDescription = '落下検知';
    } else if (sensingType === 'mcdDetected') {
      switch (eventTrigger) {
        case 'InMotionToStationary':
          alertDescription = '動 → 静 検知';
          break;
        case 'StationaryToInMotion':
          alertDescription = '静 → 動 検知';
          break;
      }
    } else if (sensingType === 'lightExposure') {
      const lightValue = value != null ? value : '-';

      switch (eventType) {
        case 'LightToDark':
          alertDescription = `明 → 暗 検出（${lightValue} Lux）`;
          break;
        case 'DarkToLight':
          alertDescription = `暗 → 明 検出（${lightValue} Lux）`;
          break;
        case 'LightTooBright':
          alertDescription = `明暗設定失敗（${lightValue} Lux）`;
          break;
        case 'OneTimeLightExposureDetected':
          alertDescription = `光検出（${lightValue} Lux）`;
          break;
      }
    } else if (this.sensingType === 'shockDetected') {
      // 衝撃検知の場合のみ、アラート発生時の閾値をラベルに表示する。
      alertDescription = `衝撃検知（${threshold ?? '-'} G）`;
    }

    return alertDescription;
  }

  private drawRoutes(markers: Marker[]) {
    // 初期化
    if (this.map.getLayer('route') !== undefined) {
      this.map.removeLayer('route');
    }
    if (this.map.getLayer('direction') !== undefined) {
      this.map.removeLayer('direction');
    }
    if (this.map.getSource('route') !== undefined) {
      this.map.removeSource('route');
    }

    if (markers.length < 2) {
      return;
    }

    // マーカーの座標を取得
    const markerCoordinates = markers.map((marker) => {
      const lngLat = marker.getLngLat();
      return [lngLat.lng, lngLat.lat];
    });

    const route = {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: markerCoordinates,
          },
        },
      ],
    };

    this.map.addSource('route', {
      type: 'geojson',
      data: route,
    });

    this.map.addLayer({
      id: 'route',
      type: 'line',
      source: 'route',
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
      },
      paint: {
        'line-color': this.routeColor,
        'line-width': 5,
      },
    });

    this.map.addLayer({
      id: 'direction',
      type: 'symbol',
      source: 'route',
      layout: {
        'symbol-placement': 'line',
        'icon-image': 'arrow-head',
        'icon-offset': [15, 0],
        'symbol-spacing': [
          'interpolate',
          ['linear'],
          ['zoom'],
          10,
          100,
          20,
          500,
        ],
      },
    });
  }

  private panToMarkers(markers: Marker[]) {
    if (markers.length < 1) {
      this.map.setCenter([139.767, 35.681]);
      this.map.setZoom(11);
      return;
    }

    // マーカーの座標を取得
    const markerCoordinates = markers.map((marker) => marker.getLngLat());

    // マーカーの座標を包括する範囲を計算
    const bounds = new LngLatBounds();
    markerCoordinates.forEach((coord) => bounds.extend(coord));

    // マップをPANしてマーカーが見えるようにする
    this.map.fitBounds(bounds, { padding: 50, maxZoom: 15 });
  }

  private createArrowHeadImage(
    size: number,
    color: string,
  ): Promise<ImageBitmap> {
    const data = `
      <svg width='${size}' height='${size}' viewBox='0 0 100 100' 
       xmlns='http://www.w3.org/2000/svg' version='1.1'><polygon fill='${color}' 
       stroke='gray' stroke-width='1' points='20,90 50,10 80,90 50,70' 
       transform='rotate(90 50 50)'/>
      </svg>
    `;
    return new Promise<ImageBitmap>((resolve) => {
      const img = new Image(size, size);
      img.src = 'data:image/svg+xml;base64,' + btoa(data);
      img.onload = () => resolve(createImageBitmap(img));
    });
  }

  private updateHighlightLocation(
    locationList: DeviceLocationViewModel[],
    sensingType: Exclude<SensingType, 'battery'>,
  ): void {
    // ステータスごとに優先順位を設定
    const statusPriority = ['Alert', 'PreAlert', '', null];

    for (const priority of statusPriority) {
      const matchedData = locationList.find(
        (data) => data[sensingType]?.status === priority,
      );
      if (matchedData) {
        this.highlightLocationList.push(matchedData);
        return;
      }
    }
  }

  private isLocationSelection(
    chartPoint: ChartPointViewModel,
  ): chartPoint is LocationSelectionViewModel {
    return 'locationTimeStamp' in chartPoint;
  }
}
